import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster as SonnerToaster } from "sonner";
import { AppProvider, useApp } from "@/context";
import { MARKETPLACE_ENABLED } from "@/flags";
import { trackPageview } from "@/behavior";
import Layout from "@/components/Layout";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PixelsLoader from "@/components/PixelsLoader";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
// Critical path — bundled into main chunk:
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Browse from "@/pages/Browse";
import Product from "@/pages/Product";
import Cart from "@/pages/Cart";
import { Login, Register } from "@/pages/Auth";
import "@/App.css";
import useSilentAutoRefresh from "@/hooks/useSilentAutoRefresh";

// Lazy-loaded routes — split into separate chunks fetched on demand.
// Reduces the initial JS bundle from ~2MB to ~400KB → 3x faster First Paint.
const Checkout = lazy(() => import("@/pages/Checkout"));
const Recycle = lazy(() => import("@/pages/Recycle"));
const Orders = lazy(() => import("@/pages/Orders").then((m) => ({ default: m.OrdersList })));
const OrderDetail = lazy(() => import("@/pages/Orders").then((m) => ({ default: m.OrderDetail })));
const AIStudio = lazy(() => import("@/pages/AIStudio"));
const AIBouquetDesigner = lazy(() => import("@/pages/AIBouquetDesigner"));
const CustomBouquetBuilder = lazy(() => import("@/pages/CustomBouquetBuilder"));
const Occasions = lazy(() => import("@/pages/Occasions"));
const MyDesigns = lazy(() => import("@/pages/MyDesigns"));
const Design = lazy(() => import("@/pages/Design"));
const Designs = lazy(() => import("@/pages/Designs"));
const DesignStudio = lazy(() => import("@/pages/DesignStudio"));
const CakeDesigner = lazy(() => import("@/pages/CakeDesigner"));
const Offers = lazy(() => import("@/pages/Offers"));
const AllDepartments = lazy(() => import("@/pages/AllDepartments"));
const GiftOccasions = lazy(() => import("@/pages/GiftOccasions"));
const Profile = lazy(() => import("@/pages/Profile"));
const Referral = lazy(() => import("@/pages/Referral"));
const Admin = lazy(() => import("@/pages/Admin"));
const Staff = lazy(() => import("@/pages/Staff"));
const StaffOps = lazy(() => import("@/pages/StaffOps"));
const ProductsEntry = lazy(() => import("@/pages/ProductsEntry"));
const OpsGo = lazy(() => import("@/pages/ops/shared").then((m) => ({ default: m.OpsGo })));
const OpsDashboard = lazy(() => import("@/pages/ops/OpsDashboard"));
const OpsBoard = lazy(() => import("@/pages/ops/OpsBoard"));
const OpsIntake = lazy(() => import("@/pages/ops/OpsIntake"));
const OpsLocation = lazy(() => import("@/pages/ops/OpsLocation"));
const OpsCards = lazy(() => import("@/pages/ops/OpsCards"));
const OpsFlowerStore = lazy(() => import("@/pages/ops/OpsFlowerStore"));
const OpsPrep = lazy(() => import("@/pages/ops/OpsPrep"));
const OpsQC = lazy(() => import("@/pages/ops/OpsQC"));
const OpsDelivery = lazy(() => import("@/pages/ops/OpsDelivery"));
const OpsReports = lazy(() => import("@/pages/ops/OpsReports"));
const DispatcherMap = lazy(() => import("@/pages/ops/DispatcherMap"));
const OpsPosRecon = lazy(() => import("@/pages/ops/OpsPosRecon"));
const PurchasesHome = lazy(() => import("@/pages/purchases/PurchasesHome"));
const NewPurchaseOrder = lazy(() => import("@/pages/purchases/NewPurchaseOrder"));
const DirectPurchase = lazy(() => import("@/pages/purchases/DirectPurchase"));
const BuyerTasks = lazy(() => import("@/pages/purchases/BuyerTasks"));
const PurchaseDetail = lazy(() => import("@/pages/purchases/PurchaseDetail"));
const SupplierDues = lazy(() => import("@/pages/purchases/SupplierDues"));
const ProcRecords = lazy(() => import("@/pages/purchases/ProcRecords"));

const OPS_ROLES = ["admin","country_admin","branch_admin","super_admin","staff","driver","cashier","employee","receiver"];
const GiftCard = lazy(() => import("@/pages/GiftCard"));
const PrintCard = lazy(() => import("@/pages/PrintCard"));
const ShareAddress = lazy(() => import("@/pages/ShareAddress"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const OrderScan = lazy(() => import("@/pages/OrderScan"));
const PayPage = lazy(() => import("@/pages/Pay"));
const OpsScan = lazy(() => import("@/pages/ops/OpsScan"));
const GroupPay = lazy(() => import("@/pages/GroupPay"));
const MerchantJoin = lazy(() => import("@/pages/MerchantJoin"));
const MerchantDashboard = lazy(() => import("@/pages/MerchantDashboard"));
const MerchantStaffLogin = lazy(() => import("@/pages/MerchantStaffLogin"));
const MerchantActivate = lazy(() => import("@/pages/MerchantActivate"));
const StoresDirectory = lazy(() => import("@/pages/StoresDirectory"));
const StorePage = lazy(() => import("@/pages/StorePage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const Branches = lazy(() => import("@/pages/Branches"));
const PrintOrder = lazy(() => import("@/pages/PrintOrder"));
const TaxInvoice = lazy(() => import("@/pages/TaxInvoice"));
const InfluencerProgram = lazy(() => import("@/pages/InfluencerProgram"));
const BloggerProducts = lazy(() => import("@/pages/BloggerProducts"));
const BloggerPicks = lazy(() => import("@/pages/BloggerPicks"));
const EarnHub = lazy(() => import("@/pages/EarnHub"));
const InvestLanding = lazy(() => import("@/pages/InvestLanding"));
const InvestApply = lazy(() => import("@/pages/InvestApply"));
const InvestDashboard = lazy(() => import("@/pages/InvestDashboard"));
const VendorPortal = lazy(() => import("@/pages/VendorPortal"));
const InfoPage = lazy(() => import("@/pages/InfoPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const OccasionLanding = lazy(() => import("@/pages/OccasionLanding"));
const RGLive = lazy(() => import("@/pages/RGLive"));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
      <div className="w-8 h-8 rounded-full border-2 border-amber-300 border-t-transparent animate-spin"
            style={{ borderColor: "#000000", borderTopColor: "transparent" }} />
    </div>
  );
}

function InnerRoutes() {
  // Detects a NEW DEPLOY via /api/version; applies it only on the user's own
  // next navigation — never reloads while someone is working in a screen.
  useSilentAutoRefresh();
  const loc = useLocation();
  // Idle prefetch: warm the lazy chunks behind bottom-nav targets so first
  // tap navigates instantly even on slow mobile networks.
  useEffect(() => {
    const t = setTimeout(() => {
      // كل وجهات الشريط السفلي والصفحات الأكثر زيارة — تُحمّل مسبقاً بالخلفية
      import("@/pages/Profile").catch(() => {});
      import("@/pages/Checkout").catch(() => {});
      import("@/pages/Offers").catch(() => {});
      import("@/pages/MyDesigns").catch(() => {});
      import("@/pages/Wallet").catch(() => {});
      import("@/pages/EarnHub").catch(() => {});
      import("@/pages/GiftOccasions").catch(() => {});
      import("@/pages/Occasions").catch(() => {});
      import("@/pages/Designs").catch(() => {});
      import("@/pages/Design").catch(() => {});
      import("@/pages/Orders").catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, []);
  // Customer behavior tracking: pageview + time-on-page per route change.
  useEffect(() => { trackPageview(loc.pathname); }, [loc.pathname]);
  // 📲 تتبع تثبيتات PWA — مرة عند الإقلاع (آمن وصامت)
  useEffect(() => {
    import("@/pwaTracker").then((m) => m.initPwaTracking()).catch(() => {});
  }, []);
  // فتح كل صفحة من أولها — تمرير للأعلى عند كل تنقل (طلب يونيو 2026)
  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);
  // Emergent OAuth callback: detect session_id in URL fragment BEFORE any other
  // routing. Per integration playbook, this must be synchronous (not in an effect)
  // to avoid race conditions with other auth checks.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (loc.hash && loc.hash.includes("session_id=")) {
    return <Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>;
  }
  // Public gift card page: no app layout (full-screen reveal UX)
  if (loc.pathname.startsWith("/gift/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes><Route path="/gift/:code" element={<GiftCard />} /></Routes>
      </Suspense>
    );
  }
  // Print pages: no app layout (print-optimized)
  if (loc.pathname.startsWith("/print/") || loc.pathname.startsWith("/print-order/") || loc.pathname.startsWith("/tax-invoice/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/print/:code" element={<PrintCard />} />
          <Route path="/print-order/:code" element={<PrintOrder />} />
          <Route path="/tax-invoice/:orderId" element={<TaxInvoice />} />
        </Routes>
      </Suspense>
    );
  }
  // Recipient address-share page: no app layout (recipient has no RG account)
  if (loc.pathname.startsWith("/share-address/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes><Route path="/share-address/:code" element={<ShareAddress />} /></Routes>
      </Suspense>
    );
  }
  // Native in-app payment screen: no app layout (full-screen, app-like)
  if (loc.pathname.startsWith("/pay/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes><Route path="/pay/:orderId" element={<PayPage />} /></Routes>
      </Suspense>
    );
  }
  // Public order tracking page (works with or without login)
  if (loc.pathname.startsWith("/track")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/track" element={<TrackOrder />} />
          <Route path="/track/:code" element={<TrackOrder />} />
        </Routes>
      </Suspense>
    );
  }
  // QR resolver — scanning an invoice QR opens the live order (staff view)
  if (loc.pathname.startsWith("/o/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes><Route path="/o/:ref" element={<OrderScan />} /></Routes>
      </Suspense>
    );
  }
  // RG LIVE — immersive full-screen live shopping (no app layout)
  if (loc.pathname === "/live" || loc.pathname.startsWith("/live/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes><Route path="/live/*" element={<RGLive />} /><Route path="/live" element={<RGLive />} /></Routes>
      </Suspense>
    );
  }
  // Public group-payment (مشاركة الدفع) page — contributors have no RG account
  if (loc.pathname.startsWith("/group/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes><Route path="/group/:code" element={<GroupPay />} /></Routes>
      </Suspense>
    );
  }
  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/departments" element={<AllDepartments />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/p/:id" element={<Product />} />
          <Route path="/design/:id" element={<Design />} />
          <Route path="/designs" element={<Designs />} />
          <Route path="/design-studio" element={<DesignStudio />} />
          <Route path="/cake-designer" element={<CakeDesigner />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/gift-occasions" element={<GiftOccasions />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/ai" element={<AIStudio />} />
          <Route path="/ai/bouquet" element={<AIBouquetDesigner />} />
          <Route path="/ai/design" element={<CustomBouquetBuilder />} />
          <Route path="/occasions" element={<Occasions />} />
          <Route path="/occasion/:slug" element={<OccasionLanding />} />
          <Route path="/my-designs" element={<MyDesigns />} />
          <Route path="/earn" element={<EarnHub />} />
          <Route path="/influencer-program" element={<InfluencerProgram />} />
          <Route path="/blogger/products" element={<BloggerProducts />} />
          <Route path="/b/:code" element={<BloggerPicks />} />
          <Route path="/recycle" element={<Recycle />} />
          <Route path="/invest" element={<InvestLanding />} />
          <Route path="/invest/apply/:planId" element={<InvestApply />} />
          <Route path="/invest/dashboard" element={<InvestDashboard />} />
          <Route path="/partner" element={<VendorPortal />} />
          {/* المتاجر الشريكة — معطلة مؤقتاً عبر flags.js (تُعاد بجعل MARKETPLACE_ENABLED=true) */}
          {MARKETPLACE_ENABLED ? (
            <>
              <Route path="/merchant/join" element={<MerchantJoin />} />
              <Route path="/merchant/login" element={<MerchantStaffLogin />} />
              <Route path="/merchant/activate" element={<MerchantActivate />} />
              <Route path="/merchant" element={<MerchantDashboard />} />
              <Route path="/stores" element={<StoresDirectory />} />
              <Route path="/store/:slug" element={<StorePage />} />
            </>
          ) : (
            <>
              <Route path="/merchant/*" element={<Navigate to="/" replace />} />
              <Route path="/merchant" element={<Navigate to="/" replace />} />
              <Route path="/stores" element={<Navigate to="/" replace />} />
              <Route path="/store/:slug" element={<Navigate to="/" replace />} />
            </>
          )}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/referral" element={<Referral />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/branches" element={<Branches />} />
          <Route path="/info" element={<InfoPage />} />
          <Route path="/info/:section" element={<InfoPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/intake" element={<Navigate to="/ops" replace />} />
          <Route path="/admin/*" element={<RequireRole roles={["admin","country_admin","branch_admin","super_admin"]}><Admin /></RequireRole>} />
          <Route path="/staff" element={<RequireRole roles={["admin","country_admin","branch_admin","super_admin","staff","driver","cashier","employee"]}><Staff /></RequireRole>} />
          <Route path="/ops" element={<RequireRole roles={OPS_ROLES}><OpsGo /></RequireRole>} />
          <Route path="/products-entry" element={<RequireRole roles={OPS_ROLES}><ProductsEntry /></RequireRole>} />
          <Route path="/ops/legacy" element={<Navigate to="/ops" replace />} />
          <Route path="/ops/dashboard" element={<RequireRole roles={OPS_ROLES}><OpsDashboard /></RequireRole>} />
          <Route path="/ops/board" element={<RequireRole roles={OPS_ROLES}><OpsBoard /></RequireRole>} />
          <Route path="/ops/intake" element={<RequireRole roles={OPS_ROLES}><OpsIntake /></RequireRole>} />
          <Route path="/ops/location" element={<RequireRole roles={OPS_ROLES}><OpsLocation /></RequireRole>} />
          <Route path="/ops/cards" element={<RequireRole roles={OPS_ROLES}><OpsCards /></RequireRole>} />
          <Route path="/ops/assign" element={<Navigate to="/ops/flower-store" replace />} />
          <Route path="/ops/flower-store" element={<RequireRole roles={OPS_ROLES}><OpsFlowerStore /></RequireRole>} />
          <Route path="/ops/prep" element={<RequireRole roles={OPS_ROLES}><OpsPrep /></RequireRole>} />
          <Route path="/ops/qc" element={<RequireRole roles={OPS_ROLES}><OpsQC /></RequireRole>} />
          <Route path="/ops/delivery" element={<RequireRole roles={OPS_ROLES}><OpsDelivery /></RequireRole>} />
          <Route path="/ops/reports" element={<RequireRole roles={OPS_ROLES}><OpsReports /></RequireRole>} />
          <Route path="/ops/map" element={<RequireRole roles={OPS_ROLES}><DispatcherMap /></RequireRole>} />
          <Route path="/ops/pos-recon" element={<RequireRole roles={OPS_ROLES}><OpsPosRecon /></RequireRole>} />
          <Route path="/ops/scan" element={<RequireRole roles={OPS_ROLES}><OpsScan /></RequireRole>} />
          <Route path="/purchases" element={<RequireRole roles={OPS_ROLES}><PurchasesHome /></RequireRole>} />
          <Route path="/purchases/new" element={<RequireRole roles={OPS_ROLES}><NewPurchaseOrder /></RequireRole>} />
          <Route path="/purchases/direct" element={<RequireRole roles={OPS_ROLES}><DirectPurchase /></RequireRole>} />
          <Route path="/purchases/records" element={<RequireRole roles={OPS_ROLES}><ProcRecords /></RequireRole>} />
          <Route path="/purchases/suppliers" element={<RequireRole roles={OPS_ROLES}><SupplierDues /></RequireRole>} />
          <Route path="/purchases/:id" element={<RequireRole roles={OPS_ROLES}><PurchaseDetail /></RequireRole>} />
          <Route path="/buy" element={<RequireRole roles={OPS_ROLES}><BuyerTasks /></RequireRole>} />
          <Route path="*" element={
            <div className="min-h-[55vh] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="not-found-page">
              <div className="text-6xl">🌸</div>
              <h1 className="text-2xl font-black">الصفحة غير موجودة</h1>
              <p className="text-sm opacity-70">الرابط الذي فتحته غير صحيح أو لم يعد متاحاً / Page not found</p>
              <a href="/" className="btn-primary px-6 py-2.5 rounded-full font-bold" data-testid="not-found-home-link">العودة للرئيسية</a>
            </div>
          } />
        </Routes>
      </Suspense>
      {/* 📲 دعوة «أضف RG لشاشتك الرئيسية» — تظهر تلقائياً لزوار الجوال فقط */}
      <PWAInstallPrompt />
    </Layout>
  );
}

/**
 * Client-side route guard. If the user is not loaded yet show a minimal
 * loader; if they don't have any of the allowed roles, redirect to /login
 * (preserving the originally requested path so we can resume after login).
 *
 * This BLOCKS Admin/Staff page mount entirely so no admin/* fetches fire
 * for non-admin users — the iter#32 "403 storm + crash overlay" fix.
 */
function RequireAuth({ children }) {
  const { user, authReady } = useApp();
  const loc = useLocation();
  if (!authReady) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm opacity-60">
        ...
      </div>
    );
  }
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  }
  return children;
}

function RequireRole({ roles, children }) {
  const { user, authReady } = useApp();
  const loc = useLocation();
  if (!authReady) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm opacity-60">
        ...
      </div>
    );
  }
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  }
  const role = (user.role || "customer").toLowerCase();
  if (!roles.includes(role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function BoundaryWithReset({ children }) {
  const location = useLocation();
  return <ChunkErrorBoundary resetKey={location.pathname}>{children}</ChunkErrorBoundary>;
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <PixelsLoader />
        {/* Toaster عام — كانت toasts تطبيق العملاء غير مرئية إطلاقاً (مركّب بالأدمن فقط) */}
        <SonnerToaster position="top-center" richColors closeButton dir="rtl"
                       toastOptions={{ style: { zIndex: 100000 } }} />
        <BoundaryWithReset>
          <InnerRoutes />
        </BoundaryWithReset>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
